"use client";

import { useState, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Building2, ExternalLink } from "lucide-react";
import { Creator, Model, SortOption } from "@/types/model";
import { filterAndSortModels } from "@/lib/api/models";
import { ModelCard } from "@/components/models/ModelCard";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Empty,
  EmptyMedia,
  EmptyHeader,
  EmptyDescription,
} from "@/components/ui/empty";
import { Ghost } from "lucide-react";

interface CreatorDetailContentProps {
  creator: Creator;
  models: Model[];
}

export function CreatorDetailContent({
  creator,
  models,
}: CreatorDetailContentProps) {
  const t = useTranslations("creatorDetail");
  const tModels = useTranslations("models");
  const [sort, setSort] = useState<SortOption>("newest");

  const sortedModels = useMemo(
    () => filterAndSortModels(models, { sort }),
    [models, sort],
  );

  return (
    <main className="flex-1">
      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* 创造者头部：图标 + 名称 + 描述 */}
        <header className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-8">
          <Avatar className="h-20 w-20 shrink-0 rounded-xl border-2 border-border bg-muted/50 sm:h-24 sm:w-24">
            <AvatarImage
              src={creator.logo}
              alt={creator.name}
              className="rounded-xl"
            />
            <AvatarFallback className="rounded-xl bg-primary/10 text-2xl text-primary">
              <Building2 className="h-10 w-10" />
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {creator.name}
            </h1>
            {creator.description && (
              <p className="mt-2 text-muted-foreground whitespace-pre-wrap break-all">
                {creator.description}
              </p>
            )}
            {creator.website && (
              <a
                href={creator.website}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {creator.website}
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
              </a>
            )}
          </div>
        </header>

        {/* 模型列表区域：排序 + 数量 + 网格 */}
        <section>
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <span className="text-sm text-muted-foreground">
              {t("modelsCount", { count: sortedModels.length })}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t("sortLabel")}
              </span>
              <Select
                value={sort}
                onValueChange={(v) => setSort(v as SortOption)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="newest">
                    {tModels("sort.newest")}
                  </SelectItem>
                  <SelectItem value="name">{tModels("sort.name")}</SelectItem>
                  <SelectItem value="price-low">
                    {tModels("sort.priceLow")}
                  </SelectItem>
                  <SelectItem value="price-high">
                    {tModels("sort.priceHigh")}
                  </SelectItem>
                  <SelectItem value="context-high">
                    {tModels("sort.contextHigh")}
                  </SelectItem>
                  <SelectItem value="latency-low">
                    {tModels("sort.latencyLow")}
                  </SelectItem>
                    <SelectItem value="throughput-high">
                      {tModels("sort.throughputHigh")}
                    </SelectItem>
                    <SelectItem value="uptime-high">
                      {tModels("sort.uptimeHigh")}
                    </SelectItem>
                  </SelectContent>
              </Select>
            </div>
          </div>

          {sortedModels.length > 0 ? (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
              {sortedModels.map((model) => (
                <ModelCard key={model.id} model={model} />
              ))}
            </div>
          ) : (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Ghost />
                </EmptyMedia>
                <EmptyDescription>{t("noModels")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </section>
      </div>
    </main>
  );
}
